import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getProductList, getProductDetails, saveProductBasicInfo } from '../services/productService';
import { getCities, getManagers, getProductTypes, getContentMainList, deleteTourProduct } from '../services/apiService';
import ProductSettingsModal from '../components/ProductSettingsModal';
import ProductTable from '../components/ProductTable';
import NewProductModal from '../components/NewProductModal';
import ProductInventoryGrid from '../components/ProductInventoryGrid';
import SmartGuideModal from '../components/SmartGuideModal';
import { PlusIcon } from '@heroicons/react/24/solid';
import { getUserInfo } from '../utils/tokenManager';
import { isDemoMode } from '../demo/demoMode';

// 투어 리스트에서 숨길 상품타입 (기타는 견적상품, 단품은 일반상품 화면에서 관리)
const HIDDEN_SUB_TYPES = ['기타', '단품'];

const ProductRegistration = ({ fixedProductSubType = '' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('basic');
  const [showForm, setShowForm] = useState(false);
  const [isNewProductOpen, setIsNewProductOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [isSmartGuideOpen, setIsSmartGuideOpen] = useState(false);
  const [smartGuideTab, setSmartGuideTab] = useState('price');
  const [smartGuideDontShow, setSmartGuideDontShow] = useState(false);
  const [contentMainList, setContentMainList] = useState(null);
  const [focusProductNo, setFocusProductNo] = useState(null);
  const [filters, setFilters] = useState({
    country: '',
    city: '',
    productType: '투어',
    productSubType: '',
    productManager: '',
    saleStatus: 'true',
    eventStatus: 'all', // 'has' | 'none' | 'all' — default: 전체
  });

  const isNoEvents = (product) => {
    try {
      const raw = product?.최종행사일;
      if (!raw) return true;
      const s = String(raw).trim();
      const d = new Date(s.includes('T') ? s : `${s}T23:59:59`);
      if (isNaN(d.getTime())) return true;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return d < today;
    } catch {
      return true;
    }
  };

  // 통합 검색 등에서 전달된 focusProductNo를 감지해 해당 상품만 보이도록 필터링
  useEffect(() => {
    const state = location && location.state ? location.state : null;
    const incomingFocus = state && state.focusProductNo != null ? String(state.focusProductNo) : null;
    if (incomingFocus) {
      setFocusProductNo(incomingFocus);
    } else {
      setFocusProductNo(null);
    }
  }, [location.state]);

  const [filterOptions, setFilterOptions] = useState({
    countries: [],
    cities: [],
    productTypes: [],
    productSubTypes: [],
    productManagers: [],
  });

  const isHotelProduct = (p) => {
    const kindCode = String(p?.상품종류코드 || '').trim();
    const kindName = String(p?.상품종류 || '').trim();
    return kindCode === '001001' || kindName.includes('호텔');
  };

  // Fetch product list when component mounts
  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    // 데모 모드에서는 튜토리얼 말풍선이 이 자리를 대신하므로 팁 모달을 띄우지 않는다.
    if (isDemoMode()) return;
    try {
      const key = 'trp_smart_guide_pref';
      const raw = window.localStorage.getItem(key);
      if (raw === 'never') {
        return;
      }
      setSmartGuideDontShow(false);
      setSmartGuideTab('price');
      setIsSmartGuideOpen(true);
    } catch (e) {
      setSmartGuideDontShow(false);
      setSmartGuideTab('price');
      setIsSmartGuideOpen(true);
    }
  }, []);

  // Populate filter options based on product list
  useEffect(() => {
    if (products.length > 0) {
      const countries = [...new Set(products.map(p => p.국가).filter(Boolean))];
      const productTypes = [...new Set(products.map(p => p.상품종류).filter(Boolean))];
      const productManagers = [...new Set(products.map(p => p.상품담당).filter(Boolean))];
      setFilterOptions(prev => ({ ...prev, countries, productTypes, productManagers }));
    }
  }, [products]);

  // Update city and product sub-type options based on selection (product type fixed to '투어')
  useEffect(() => {
    if (filters.country) {
      const cities = [...new Set(products
        .filter(p => p.국가 === filters.country)
        .map(p => p.도시)
        .filter(Boolean))];
      setFilterOptions(prev => ({ ...prev, cities }));
    } else {
      setFilterOptions(prev => ({ ...prev, cities: [] }));
      if (filters.city !== '') setFilters(prev => ({...prev, city: ''}));
    }

    const productSubTypes = [...new Set(products
      .filter(p => !isHotelProduct(p))
      .map(p => p.상품타입)
      .filter(Boolean))].filter((st) => {
      if (!fixedProductSubType) return true;
      return String(st) === String(fixedProductSubType);
    });

    const visibleProductSubTypes = fixedProductSubType
      ? productSubTypes
      : productSubTypes.filter((st) => !HIDDEN_SUB_TYPES.includes(String(st)));
    setFilterOptions(prev => ({ ...prev, productSubTypes: visibleProductSubTypes }));
    if (fixedProductSubType) {
      // keep fixed subtype enforced
      if (String(filters.productSubType) !== String(fixedProductSubType)) {
        setFilters(prev => ({ ...prev, productSubType: fixedProductSubType }));
      }
    } else if (!visibleProductSubTypes.includes(filters.productSubType)) {
      // reset if current sub-type no longer valid
      setFilters(prev => ({ ...prev, productSubType: '' }));
    }
  }, [filters.country, products]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDeleteProduct = async (product) => {
    try {
      const productNo = product?.상품번호;
      if (!productNo) return;
      const ok = window.confirm('삭제된 상품은 복원할 수 없습니다. 정말 삭제하시겠습니까?');
      if (!ok) return;
      setLoading(true);
      await deleteTourProduct({ productNo });
      await fetchProducts();
    } catch (e) {
      console.error('상품 삭제 실패:', e);
      alert('상품 삭제에 실패했습니다. 잠시 후 다시 시도해주세요.');
    } finally {
      setLoading(false);
    }
  };

  // Parse product creation datetime safely (expects formats like "YYYY-MM-DD HH:mm" or ISO)
  const getCreatedAt = (product) => {
    try {
      const raw = product?.생성일시 || product?.등록일시 || product?.createdAt || '';
      if (!raw) return 0;
      const str = String(raw).trim();
      // Normalize to ISO-like string for reliable parsing
      const iso = str.includes('T') ? str : (str.length === 16 ? str.replace(' ', 'T') + ':00' : str.replace(' ', 'T'));
      const t = Date.parse(iso);
      return isNaN(t) ? 0 : t;
    } catch {
      return 0;
    }
  };

  // Function to fetch products from API
  const fetchProducts = async () => {
    setLoading(true);
    try {
      const { companyNo, userNo } = getUserInfo() || {};
      if (!companyNo || !userNo) {
        alert('로그인이 필요합니다. 다시 로그인해 주세요.');
        navigate('/login');
        return;
      }
      const productData = await getProductList(companyNo, userNo);
      setProducts(productData);
      setError(null);
    } catch (err) {
      setError('상품 목록을 불러오는데 실패했습니다.');
      console.error('Failed to fetch products:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSettingsClick = async (product) => {
    setModalLoading(true);
    try {
      const { companyNo } = getUserInfo() || {};
      const details = await getProductDetails(product.상품번호, companyNo);
      if (details) {
        setSelectedProduct(details);
        setIsModalOpen(true);
      } else {
        setError('상품 상세 정보를 찾을 수 없습니다.');
      }
    } catch (err) {
      setError('상품 상세 정보를 불러오는데 실패했습니다.');
      console.error('Failed to fetch product details:', err);
    } finally {
      setModalLoading(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedProduct(null);
  };

  // 모달 닫힘 전 트리거에서 호출되어 콘텐츠 메인 리스트를 최신화
  const handlePreviewRefresh = async (productNo) => {
    try {
      if (!productNo) return;
      const refreshed = await getContentMainList({ productNo });
      setContentMainList(refreshed);
    } catch (e) {
      console.error('콘텐츠 메인 리스트 갱신 실패:', e);
    }
  };

  const handleSmartGuideClose = () => {
    try {
      const key = 'trp_smart_guide_pref';
      if (smartGuideDontShow) {
        window.localStorage.setItem(key, 'never');
      } else {
        window.localStorage.removeItem(key);
      }
    } catch (e) {
    }
    setIsSmartGuideOpen(false);
  };

  // 상품 카드에서 판매여부 토글 처리
  const handleToggleSale = async (product, nextSale) => {
    try {
      setLoading(true);
      const { companyNo, userNo } = getUserInfo() || {};
      if (!companyNo || !userNo) { alert('로그인이 필요합니다.'); navigate('/login'); return; }
      const productNo = product.상품번호;

      // 최신 상세정보 조회 (기본정보용 베이스)
      const details = await getProductDetails(productNo, companyNo).catch(() => null);
      const original = details || {};

      // @기본정보 구성: 기존 값 유지 + 노출여부만 변경
      const basicInfo = {
        ...original,
        상품번호: productNo,
        노출여부: !!nextSale,
      };

      const keywordsStr = Array.isArray(original.키워드)
        ? original.키워드.join(',')
        : String(original.키워드 || '');

      await saveProductBasicInfo({ companyNo, userNo, productNo, basicInfo, keywords: keywordsStr });

      // 목록 새로고침으로 카드 상태 동기화
      await fetchProducts();
    } catch (err) {
      console.error('판매여부 변경 실패:', err);
      setError('판매여부 변경에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async (updatedProduct) => {
    try {
      setModalLoading(true);
      const { companyNo, userNo } = getUserInfo() || {};
      if (!companyNo || !userNo) { alert('로그인이 필요합니다.'); navigate('/login'); return false; }
      const productNo = updatedProduct.상품번호 || selectedProduct?.상품번호;

      // 이름->코드 매핑 준비 (필요 시에만 호출)
      const [cities, managers, productTypes] = await Promise.all([
        getCities().catch(() => []),
        getManagers().catch(() => []),
        getProductTypes().catch(() => []),
      ]);

      const findByName = (list, name) => Array.isArray(list) ? list.find(i => i.코드명 === name) : undefined;

      // 원본 상세정보를 기반으로 @기본정보 구성
      const original = selectedProduct || {};

      // 키워드 처리: 입력 문자열을 "," 기준 split -> trim -> 빈값 제외
      const keywordStr = (updatedProduct.키워드 || '').trim();
      const keywordArr = keywordStr
        ? keywordStr.split(',').map(k => k.trim()).filter(Boolean)
        : [];

      // 도시 매핑
      const cityName = updatedProduct.도시 ?? original.도시;
      const cityItem = findByName(cities, cityName);
      const cityNo = cityItem?.코드 ?? original.도시번호;

      // 상품타입 매핑
      const productTypeName = updatedProduct.상품타입 ?? original.상품타입;
      const productTypeItem = findByName(productTypes, productTypeName);
      const productTypeCode = productTypeItem?.코드 ?? original.상품타입코드;

      // 담당자 매핑 (상품담당 / 예약담당)
      const pdName = updatedProduct.상품담당 ?? original.상품담당;
      const rdName = updatedProduct.예약담당 ?? original.예약담당;
      const pdItem = findByName(managers, pdName);
      const rdItem = findByName(managers, rdName);
      const pdNo = pdItem?.코드 ?? original.상품담당번호;
      const rdNo = rdItem?.코드 ?? original.예약담당번호;

      // @기본정보: 원본 유지 + 수정 필드 반영 (미노출 필드는 원본 그대로 강제 보존)
      const basicInfo = {
        ...original,
        상품번호: productNo,
        상품명: updatedProduct.상품명 ?? original.상품명,
        도시번호: cityNo,
        도시: cityName,
        상품타입: productTypeName,
        상품타입코드: productTypeCode,
        상품코드: updatedProduct.상품코드 ?? original.상품코드, // 편집값 반영
        상품코드룰사용: original.상품코드룰사용 ?? false,
        상품담당: pdName,
        상품담당번호: pdNo,
        예약담당: rdName,
        예약담당번호: rdNo,
        디폴트수배담당: (original.디폴트수배담당 ?? original.예약담당 ?? original.상품담당) || '예약담당',
        소요타입: original.소요타입 ?? original.소요시간타입 ?? original.소요단위 ?? '',
        소요: original.소요 ?? original.소요단위 ?? '',
        소요시간: updatedProduct.소요시간 ?? original.소요시간 ?? 0,
        통화코드: original.통화코드 ?? '',
        통화: original.통화 ?? '',
        가이드타입: original.가이드타입 ?? '',
        가이드타입코드: original.가이드타입코드 ?? '',
        카테고리: original.카테고리 ?? '',
        카테고리코드: original.카테고리코드 ?? '',
        노출여부: typeof updatedProduct.노출여부 === 'boolean' ? updatedProduct.노출여부 : (original.노출여부 ?? false),
        키워드: keywordArr,
        상품뱃지: updatedProduct.상품뱃지 ?? original.상품뱃지,
      };

      // 저장 호출
      const result = await saveProductBasicInfo({ companyNo, userNo, productNo, basicInfo, keywords: keywordArr.join(',') });

      // 저장 후에는 조회 프로시저로 최신 상세 재조회 (키워드 포함)
      const refreshed = await getProductDetails(productNo, companyNo);
      if (refreshed) {
        setSelectedProduct(refreshed);
      }
      // 목록도 최신화
      await fetchProducts();
      alert('성공적으로 저장되었습니다.');

      return true;
    } catch (err) {
      setError('저장에 실패했습니다. 다시 시도해주세요.');
      console.error('Failed to save product details:', err);

      return false;
    } finally {
      setModalLoading(false);
    }
  };

  // Filter products based on search term, filters, and optional focused product
  const filteredProducts = products.filter(product => {
    // focusProductNo가 지정된 경우, 다른 검색/필터 조건은 무시하고 해당 상품만 표시
    if (focusProductNo != null) {
      const pNo = product && product.상품번호 != null ? String(product.상품번호) : null;
      return pNo != null && pNo === String(focusProductNo);
    }

    const countryMatch = !filters.country || product.국가 === filters.country;
    const cityMatch = !filters.city || product.도시 === filters.city;
    const productTypeMatch = !isHotelProduct(product);
    const targetSubType = fixedProductSubType ? fixedProductSubType : filters.productSubType;
    const productSubTypeMatch = !targetSubType || product.상품타입 === targetSubType;
    const hideOtherSubType = !fixedProductSubType;
    const visibleSubTypeMatch = !hideOtherSubType || !HIDDEN_SUB_TYPES.includes(String(product.상품타입));
    const productManagerMatch = !filters.productManager || String(product.상품담당 || '') === String(filters.productManager);
    const saleStatusMatch = filters.saleStatus === 'all' || (product.판매여부 ? 'true' : 'false') === filters.saleStatus;
    const hasEvents = !isNoEvents(product);
    const eventStatusMatch =
      filters.eventStatus === 'all' ||
      (filters.eventStatus === 'has' ? hasEvents : !hasEvents);

    return countryMatch && cityMatch && productTypeMatch && productSubTypeMatch && visibleSubTypeMatch && productManagerMatch && saleStatusMatch && eventStatusMatch;
  });

  // Summary counts for 현황 모달
  const summary = {
    total: filteredProducts.length,
    onSale: filteredProducts.filter(p => !!p.판매여부).length,
    offSale: filteredProducts.filter(p => !p.판매여부).length,
  };

  const TIP_SEQUENCE = ['price', 'product', 'email', 'schedule', 'consult', 'consult-flow'];
  const currentTipIndexRaw = TIP_SEQUENCE.indexOf(smartGuideTab);
  const currentTipIndex = currentTipIndexRaw === -1 ? 0 : currentTipIndexRaw;
  const hasPrevTip = TIP_SEQUENCE.length > 1;
  const hasNextTip = TIP_SEQUENCE.length > 1;

  const handlePrevTip = () => {
    if (!hasPrevTip) return;
    const nextIndex = (currentTipIndex - 1 + TIP_SEQUENCE.length) % TIP_SEQUENCE.length;
    setSmartGuideTab(TIP_SEQUENCE[nextIndex]);
  };

  const handleNextTip = () => {
    if (!hasNextTip) return;
    const nextIndex = (currentTipIndex + 1) % TIP_SEQUENCE.length;
    setSmartGuideTab(TIP_SEQUENCE[nextIndex]);
  };

  return (
    <div className="h-full min-h-0 flex flex-col">
      {/* Modals */}
      {isModalOpen && (
        <ProductSettingsModal
          product={selectedProduct}
          onClose={handleCloseModal}
          onSave={handleSaveSettings}
          loading={modalLoading}
          onPreviewRefresh={handlePreviewRefresh}
        />
      )}
      {isNewProductOpen && (
        <NewProductModal
          open={isNewProductOpen}
          onClose={() => setIsNewProductOpen(false)}
          fixedProductTypeName={fixedProductSubType ? String(fixedProductSubType) : ''}
          onCreated={({ productNo }) => {
            setIsNewProductOpen(false);
            navigate(`/detail-settings/${productNo}`);
          }}
        />
      )}

      <SmartGuideModal
        open={isSmartGuideOpen}
        tab={smartGuideTab}
        onTabChange={setSmartGuideTab}
        dontShow={smartGuideDontShow}
        onChangeDontShow={setSmartGuideDontShow}
        onClose={handleSmartGuideClose}
        onPrevTip={handlePrevTip}
        onNextTip={handleNextTip}
        hasPrevTip={hasPrevTip}
        hasNextTip={hasNextTip}
      />

      {/* Filter and Search Bar */}
      <div className="mb-4">
        <div className="rounded-2xl bg-white p-5">
          <div className="flex flex-wrap items-end gap-3 md:gap-4">
            {/* Country Filter */}
            <div className="w-full sm:w-[11rem]">
              <label htmlFor="country-filter" className="block text-[13px] font-semibold text-gray-600 mb-1">국가</label>
              <select id="country-filter" name="country" value={filters.country} onChange={handleFilterChange} className="block w-full h-9 rounded-lg border border-gray-300 bg-white px-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 outline-none">
                <option value="">전체</option>
                {filterOptions.countries.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>

            {/* City Filter (shown only when country is selected) */}
            {filters.country && (
              <div className="w-full sm:w-[11rem]">
                <label htmlFor="city-filter" className="block text-[13px] font-semibold text-gray-600 mb-1">도시</label>
                <select id="city-filter" name="city" value={filters.city} onChange={handleFilterChange} className="block w-full h-9 rounded-lg border border-gray-300 bg-white px-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 outline-none">
                  <option value="">전체</option>
                  {filterOptions.cities.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Product Sub-Type Filter */}
            <div className="w-full sm:w-[11rem]">
              <label htmlFor="productSubType-filter" className="block text-[13px] font-semibold text-gray-600 mb-1">상품 타입</label>
              <select
                id="productSubType-filter"
                name="productSubType"
                value={fixedProductSubType ? String(fixedProductSubType) : filters.productSubType}
                onChange={handleFilterChange}
                disabled={!!fixedProductSubType}
                className="block w-full h-9 rounded-lg border border-gray-300 bg-white px-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 outline-none"
              >
                {!fixedProductSubType && <option value="">전체</option>}
                {filterOptions.productSubTypes.map((st) => (
                  <option key={st} value={st}>{st}</option>
                ))}
              </select>
            </div>

            {/* Product Manager Filter */}
            <div className="w-full sm:w-[11rem]">
              <label htmlFor="productManager-filter" className="block text-[13px] font-semibold text-gray-600 mb-1">상품담당</label>
              <select
                id="productManager-filter"
                name="productManager"
                value={filters.productManager}
                onChange={handleFilterChange}
                className="block w-full h-9 rounded-lg border border-gray-300 bg-white px-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 outline-none"
              >
                <option value="">전체</option>
                {filterOptions.productManagers.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>

            <div className="flex flex-col" data-tour="sale-filter">
              <span className="block text-[13px] font-semibold text-gray-600 mb-1">판매 여부</span>
              <div className="flex items-center gap-0.5 rounded-lg bg-gray-100 p-1">
                <button
                  type="button"
                  className={`h-7 px-3 text-[13px] font-semibold rounded-md transition-colors ${filters.saleStatus === 'all' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-900'}`}
                  onClick={() => setFilters((p) => ({ ...p, saleStatus: 'all' }))}
                >전체</button>
                <button
                  type="button"
                  className={`h-7 px-3 text-[13px] font-semibold rounded-md transition-colors ${filters.saleStatus === 'true' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-900'}`}
                  onClick={() => setFilters((p) => ({ ...p, saleStatus: 'true' }))}
                >판매중</button>
                <button
                  type="button"
                  className={`h-7 px-3 text-[13px] font-semibold rounded-md transition-colors ${filters.saleStatus === 'false' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-900'}`}
                  onClick={() => setFilters((p) => ({ ...p, saleStatus: 'false' }))}
                >판매중지</button>
              </div>
            </div>

            <div className="hidden sm:block self-stretch w-px bg-gray-200 my-1" aria-hidden="true" />

            <div className="flex flex-col" data-tour="event-filter">
              <span className="block text-[13px] font-semibold text-gray-600 mb-1">행사 여부</span>
              <div className="flex items-center gap-0.5 rounded-lg bg-gray-100 p-1">
                <button
                  type="button"
                  className={`h-7 px-3 text-[13px] font-semibold rounded-md transition-colors ${filters.eventStatus === 'all' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-900'}`}
                  onClick={() => setFilters((p) => ({ ...p, eventStatus: 'all' }))}
                >전체</button>
                <button
                  type="button"
                  className={`h-7 px-3 text-[13px] font-semibold rounded-md transition-colors ${filters.eventStatus === 'has' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-900'}`}
                  onClick={() => setFilters((p) => ({ ...p, eventStatus: 'has' }))}
                >있음</button>
                <button
                  type="button"
                  className={`h-7 px-3 text-[13px] font-semibold rounded-md transition-colors ${filters.eventStatus === 'none' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-900'}`}
                  onClick={() => setFilters((p) => ({ ...p, eventStatus: 'none' }))}
                >없음</button>
              </div>
            </div>

            {/* Search + Actions */}
            <div className="flex items-center gap-2 sm:ml-auto">
              <button type="button" data-tour="product-status" onClick={() => setIsStatusModalOpen(true)} className="inline-flex items-center justify-center h-9 px-3.5 whitespace-nowrap text-sm font-semibold rounded-lg border border-gray-300 bg-white text-gray-900 hover:bg-gray-50 transition-colors">상품등록 현황</button>
              <button data-tour="smart-product" onClick={() => navigate('/quick-wizard')} className="inline-flex items-center justify-center h-9 px-3.5 whitespace-nowrap text-sm font-semibold rounded-lg bg-gray-100 text-gray-900 hover:bg-gray-200 transition-colors" title="최소 정보로 빠르게 상품 등록">스마트 상품 등록</button>
              <button data-tour="new-product" onClick={() => setIsNewProductOpen(true)} className="inline-flex items-center justify-center h-9 px-3.5 whitespace-nowrap text-sm font-semibold rounded-lg text-white bg-blue-600 hover:bg-blue-700 transition-colors">
                <PlusIcon className="-ml-1 mr-2 h-4 w-4" />
                새 상품 등록
              </button>
            </div>
          </div>
        </div>
      </div>

      {loading && (
        <div className="flex justify-center items-center py-12">
          <div className="flex flex-col items-center gap-3">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-gray-600"></div>
            <p className="text-sm text-gray-500">상품 목록을 불러오는 중...</p>
          </div>
        </div>
      )}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {!loading && !error && (
        <div className="flex-1 min-h-0 rounded-2xl bg-white p-5 overflow-hidden" data-tour="product-grid">
          {filteredProducts.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-sm text-gray-500">검색 결과가 없습니다.</p>
            </div>
          ) : (
            <ProductInventoryGrid
              rowData={filteredProducts}
              onToggleSale={handleToggleSale}
              onOpenBasicSettings={handleSettingsClick}
              onOpenPriceStock={(p) => navigate(`/price-stock/${p?.상품번호 || ''}`)}
              onOpenDetailSettings={(p) => navigate(`/detail-settings/${p?.상품번호 || ''}`)}
              onOpenOrders={(p) => {
                const productNo = p?.상품번호 || '';
                navigate(`/orders/${encodeURIComponent(productNo)}`, { state: { productName: p?.상품명 || '', productNo: p?.상품번호 || null, productCode: p?.상품코드 || null } });
              }}
              onDeleteProduct={handleDeleteProduct}
              onNoteSaved={(prod, note) => {
                setProducts((prev) => Array.isArray(prev) ? prev.map((p) => {
                  const idA = p?.상품번호 || p?.상품코드;
                  const idB = prod?.상품번호 || prod?.상품코드;
                  if (idA && idB && String(idA) === String(idB)) {
                    return { ...p, 상품비고: note };
                  }
                  return p;
                }) : prev);
              }}
            />
          )}
        </div>
      )}

      {/* 상품등록 현황 모달 */}
      {isStatusModalOpen && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30" onClick={() => setIsStatusModalOpen(false)} />
          <div className="relative z-10 w-[min(1400px,98vw)] max-h-[90vh] overflow-auto bg-white rounded-xl shadow-xl border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base font-semibold text-gray-800">상품등록 현황</h2>
              <button type="button" className="px-2 py-1 text-sm rounded border bg-white hover:bg-gray-50" onClick={() => setIsStatusModalOpen(false)}>닫기</button>
            </div>
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className="inline-flex items-center gap-2 px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800 border">전체 <strong className="ml-1">{summary.total}</strong></span>
              <span className="inline-flex items-center gap-2 px-2 py-1 text-xs rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">판매중 <strong className="ml-1">{summary.onSale}</strong></span>
              <span className="inline-flex items-center gap-2 px-2 py-1 text-xs rounded-full bg-gray-50 text-gray-700 border border-gray-200">중지 <strong className="ml-1">{summary.offSale}</strong></span>
            </div>
            <ProductTable
              products={filteredProducts}
              onToggleSale={handleToggleSale}
              onOpenSettings={handleSettingsClick}
              onOpenOrders={(p) => {
                const productNo = p?.상품번호 || '';
                navigate(`/orders/${encodeURIComponent(productNo)}`, { state: { productName: p?.상품명 || '', productNo: p?.상품번호 || null, productCode: p?.상품코드 || null } });
              }}
              onOpenPriceStock={(p) => navigate(`/price-stock/${p?.상품번호 || ''}`)}
              onOpenDetailSettings={(p) => navigate(`/detail-settings/${p?.상품번호 || ''}`)}
              showOrdersButton
              nameLinkNeutral
              enableColumnFilters
              dateOnly
              singleDateFilter
              disablePagination
              showModalExtraColumns
              actionsInName
              pageSize={20}
              onNoteSaved={(prod, note) => {
                setProducts((prev) => Array.isArray(prev) ? prev.map((p) => {
                  const idA = p?.상품번호 || p?.상품코드;
                  const idB = prod?.상품번호 || prod?.상품코드;
                  if (idA && idB && String(idA) === String(idB)) {
                    return { ...p, 상품비고: note };
                  }
                  return p;
                }) : prev);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};
export default ProductRegistration;
